/**
 * Grouped Bloks configuration cart actions: remove or scale quantity.
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';
import {
  buildMultiConfigCartAttributes,
  collectConfigsFromCartItems,
} from './bloks-konfigurator-cart-attributes.js';

function cartUpdateUrl() {
  const base = Theme?.routes?.cart_update_url || '/cart/update';
  return base.endsWith('.js') ? base : `${base}.js`;
}

function cartJsUrl() {
  return `${Theme?.routes?.cart_url || '/cart'}.js`;
}

function cartItemsComponents() {
  return document.querySelectorAll('cart-items-component');
}

function sectionsToUpdate() {
  const sections = new Set();
  cartItemsComponents().forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sections.add(item.dataset.sectionId);
    }
  });
  return Array.from(sections);
}

function setCartBusy(busy) {
  cartItemsComponents().forEach((el) => {
    el.classList.toggle('cart-items-disabled', busy);
  });
}

/**
 * @param {Record<string, number>} updates
 * @param {Record<string, string>} [attributes]
 * @param {'remove' | 'update'} action
 * @param {{ id: string, quantity: number }[]} lines
 */
async function postCartUpdate(updates, attributes, action, lines) {
  const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines,
      promise: deferredUpdatePromise.promise,
    })
  );

  setCartBusy(true);

  try {
    /** @type {Record<string, unknown>} */
    const body = {
      updates,
      sections: sectionsToUpdate().join(','),
      sections_url: window.location.pathname,
    };
    if (attributes && Object.keys(attributes).length > 0) {
      body.attributes = attributes;
    }

    const response = await fetch(
      cartUpdateUrl(),
      fetchConfig('json', {
        body: JSON.stringify(body),
      })
    );
    const data = await response.json();

    if (data.status || data.errors) {
      const message = data.description || data.message || data.errors || 'Cart update failed';
      deferredUpdatePromise.reject(new Error(message));
      throw new Error(message);
    }

    deferredUpdatePromise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: {
        sections: data.sections,
        items: data.items,
        itemCount: data.item_count,
        source: 'bloks-konfigurator-cart-groups',
        didError: false,
      },
    });

    return data;
  } catch (error) {
    try {
      deferredUpdatePromise.reject(error);
    } catch {
      // Promise already settled
    }
    throw error;
  } finally {
    setCartBusy(false);
  }
}

async function removeConfigurationGroup(button) {
  const keys = String(button.getAttribute('data-bloks-line-keys') || '')
    .split('|')
    .map((key) => key.trim())
    .filter(Boolean);

  if (keys.length === 0) return;

  const keySet = new Set(keys);
  /** @type {Record<string, number>} */
  const updates = {};
  for (const key of keys) {
    updates[key] = 0;
  }

  const card = button.closest('[data-bloks-config-id]');
  const configId = card instanceof HTMLElement ? card.dataset.bloksConfigId || '' : '';

  const cartResponse = await fetch(cartJsUrl());
  const cart = await cartResponse.json();
  const remainingItems = (cart.items || []).filter((item) => !keySet.has(item.key));
  const attributes = buildMultiConfigCartAttributes(
    collectConfigsFromCartItems(remainingItems),
    cart.attributes || {},
  );
  if (configId) {
    attributes[`_bloks_cfg_qty_${configId}`] = '';
  }

  await postCartUpdate(
    updates,
    attributes,
    'remove',
    keys.map((id) => ({ id, quantity: 0 }))
  );
}

/**
 * @param {HTMLElement} card
 * @param {number} nextQty
 */
async function scaleConfigurationGroup(card, nextQty) {
  const qty = Math.max(1, Math.round(nextQty));
  let recipe = [];
  try {
    recipe = JSON.parse(card.dataset.bloksRecipe || '[]');
  } catch {
    recipe = [];
  }

  if (!Array.isArray(recipe) || recipe.length === 0) return;

  /** @type {Record<string, number>} */
  const updates = {};
  for (const entry of recipe) {
    const key = String(entry?.key || '').trim();
    const unitQty = Number(entry?.qty);
    if (!key || !Number.isFinite(unitQty) || unitQty < 1) continue;
    updates[key] = unitQty * qty;
  }

  if (Object.keys(updates).length === 0) return;

  const configId = card.dataset.bloksConfigId || '';
  /** @type {Record<string, string>} */
  const attributes = {};
  if (configId) {
    attributes[`_bloks_cfg_qty_${configId}`] = String(qty);
  }

  await postCartUpdate(
    updates,
    attributes,
    'update',
    Object.entries(updates).map(([id, quantity]) => ({ id, quantity }))
  );
}

/**
 * @param {HTMLElement} button
 */
async function shareConfiguration(button) {
  const url = String(button.getAttribute('data-share-url') || '').trim();
  if (!url) return;

  const title = String(button.getAttribute('data-share-title') || document.title);
  const absoluteUrl = new URL(url, window.location.origin).toString();

  try {
    if (typeof navigator.share === 'function') {
      await navigator.share({ title, url: absoluteUrl });
      return;
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
  }

  try {
    await navigator.clipboard.writeText(absoluteUrl);
    const label = button.querySelector('[data-bloks-share-label]');
    if (!(label instanceof HTMLElement)) return;
    const original = label.getAttribute('data-label-original') || label.textContent || '';
    label.setAttribute('data-label-original', original.trim());
    label.textContent =
      globalThis.Theme?.translations?.bloks_konfigurator_share_copied || 'Link copied';
    window.setTimeout(() => {
      label.textContent = label.getAttribute('data-label-original') || original;
    }, 1800);
  } catch (error) {
    console.error('[BloksKonfiguratorCart] share configuration failed', error);
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const shareButton = target.closest('[data-bloks-config-share]');
  if (shareButton instanceof HTMLElement) {
    event.preventDefault();
    shareConfiguration(shareButton).catch((error) => {
      console.error('[BloksKonfiguratorCart] share configuration failed', error);
    });
    return;
  }

  const removeButton = target.closest('[data-bloks-config-remove]');
  if (removeButton instanceof HTMLElement) {
    event.preventDefault();
    if (removeButton.dataset.busy === 'true') return;
    removeButton.dataset.busy = 'true';
    removeConfigurationGroup(removeButton)
      .catch((error) => {
        console.error('[BloksKonfiguratorCart] remove configuration failed', error);
      })
      .finally(() => {
        removeButton.dataset.busy = 'false';
      });
    return;
  }

  const qtyButton = target.closest('[data-bloks-config-qty]');
  if (!(qtyButton instanceof HTMLElement)) return;

  const card = qtyButton.closest('[data-bloks-config-id]');
  if (!(card instanceof HTMLElement)) return;

  const input = card.querySelector('[data-bloks-config-qty-input]');
  if (!(input instanceof HTMLInputElement)) return;

  const current = Number.parseInt(input.value, 10) || 1;
  const direction = qtyButton.getAttribute('data-bloks-config-qty');
  const nextQty = direction === 'minus' ? current - 1 : current + 1;
  if (nextQty < 1) return;

  event.preventDefault();
  if (card.dataset.busy === 'true') return;
  card.dataset.busy = 'true';
  input.value = String(nextQty);
  scaleConfigurationGroup(card, nextQty)
    .catch((error) => {
      input.value = String(current);
      console.error('[BloksKonfiguratorCart] scale configuration failed', error);
    })
    .finally(() => {
      card.dataset.busy = 'false';
    });
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-bloks-config-qty-input')) return;

  const card = target.closest('[data-bloks-config-id]');
  if (!(card instanceof HTMLElement)) return;

  const current = Number.parseInt(card.dataset.bloksQty || '1', 10) || 1;
  const nextQty = Number.parseInt(target.value, 10);
  if (!Number.isFinite(nextQty) || nextQty < 1) {
    target.value = String(current);
    return;
  }
  if (nextQty === current) return;

  if (card.dataset.busy === 'true') return;
  card.dataset.busy = 'true';
  scaleConfigurationGroup(card, nextQty)
    .catch((error) => {
      target.value = String(current);
      console.error('[BloksKonfiguratorCart] scale configuration failed', error);
    })
    .finally(() => {
      card.dataset.busy = 'false';
    });
});
